/**
 * Documentation Service
 *
 * REST client for the AI-powered documentation generation API.
 * Uses the shared axiosInstance (with auth token injection) but overrides
 * the baseURL to point at the docs microservice.
 *
 * API: POST https://soql-graph-service-56307224995.us-central1.run.app/docs/generate
 */

import { createAxiosInstance } from '@/dataProvider/axios';
import { env } from '@/config/env';
import type {
  GenerateDocumentationInput,
  GenerateDocumentationResponse,
} from './types';

export * from './types';

// ============================================================================
// Axios instance for the docs microservice
// Routed through backend proxy which injects GCP OIDC token
// ============================================================================

/**
 * Dedicated axios instance for the documentation microservice.
 * Inherits JWT auth token injection from createAxiosInstance.
 */
const docsAxiosInstance = createAxiosInstance(`${env.API_URL}/proxy/graph`);

// ============================================================================
// Service functions
// ============================================================================

/**
 * Generate AI documentation for a Salesforce metadata component.
 *
 * @param input   - Request payload (org_id, file_id, class_name, metadata_type, …)
 * @param orgId   - Organization UUID — sent as X-Organization-ID header
 * @param userId  - User ID — sent as X-User-ID header (optional)
 * @returns Generated documentation response
 */
export async function generateDocumentation(
  input: GenerateDocumentationInput,
  orgId: string,
  userId?: string,
): Promise<GenerateDocumentationResponse> {
  const response = await docsAxiosInstance.post<GenerateDocumentationResponse>(
    '/docs/generate',
    {
      org_id: input.org_id,
      file_id: input.file_id,
      class_name: input.class_name,
      metadata_type: input.metadata_type,
      user_id: userId ?? input.user_id ?? '',
      enrich: input.enrich ?? true,
    },
    {
      headers: {
        'X-Organization-ID': orgId,
        'X-User-ID': userId ?? '',
      },
    },
  );

  return response.data;
}
